import { directoryClient } from 'shared/domain/directory/directoryClient';
import { documentationClient } from 'shared/domain/documentation/documentationClient';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { filterItemsByName } from 'shared/utils/search';
import { DirectoryModel } from 'shared/domain/directory/directoryModel';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { hasValidId } from 'shared/utils/id/entityIds';
import {
  getDirectoryLocalIdFromRowId,
  getDocumentationLocalIdFromRowId,
} from './rowId';
import { findDisplayedVersion } from 'shared/domain/documentation/documentationVersionHelpers';

type DirectoryContent = {
  directories: DirectoryModel[];
  files: DocumentationModel[];
};

type DirectoryNode = {
  id: string;
  name: string;
};

const EMPTY_CONTENT: DirectoryContent = {
  directories: [],
  files: [],
};

export interface UseDocumentationBrowserStateOptions {
  isOpen: boolean;
  selectableDocumentation?: boolean;
  singleDocumentationSelection?: boolean;
  /** Whether a documentation without a file to show (no current version) cannot be selected; `true` when left out. */
  requireDisplayedVersion?: boolean;
  singleClickOpensDirectory?: boolean;
  initialDirectoryId?: string;
  initialDirectoryName?: string;
  initialDocumentationLocalId?: string;
}

export function useDocumentationBrowserState({
  isOpen,
  selectableDocumentation = false,
  singleDocumentationSelection = true,
  requireDisplayedVersion = true,
  singleClickOpensDirectory = true,
  initialDirectoryId,
  initialDirectoryName,
  initialDocumentationLocalId,
}: UseDocumentationBrowserStateOptions) {
  // Local cache for browser state - independent of main DataGrid cache
  const [browserCache, setBrowserCache] = useState<Map<string, DirectoryContent>>(new Map());

  const getDirectoryContent = useCallback(
    async (directoryId: string): Promise<DirectoryContent> => {
      // Check local browser cache first
      const cached = browserCache.get(directoryId);
      if (cached) {
        return cached;
      }

      // Fetch fresh data from SW
      const [directories, documentations] = await Promise.all([
        directoryClient.getChildren(directoryId),
        documentationClient.getChildren(directoryId),
      ]);

      const content: DirectoryContent = {
        directories: directories.filter(hasValidId),
        files: documentations.filter(hasValidId),
      };

      // Store in local browser cache
      setBrowserCache((prev) => {
        const next = new Map(prev);
        next.set(directoryId, content);
        return next;
      });

      return content;
    },
    [browserCache]
  );

  const invalidateDirectory = useCallback(
    (directoryId: string) => {
      setBrowserCache((prev) => {
        const next = new Map(prev);
        next.delete(directoryId);
        return next;
      });
    },
    []
  );

  const [path, setPath] = useState<DirectoryNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchPhrase, setSearchPhrase] = useState('');
  const [selectedDirectoryId, setSelectedDirectoryId] = useState<string | null>(
    null
  );
  const [selectedDocumentationLocalId, setSelectedDocumentationLocalId] =
    useState<string | null>(null);

  const currentDirectory = path.at(-1);
  const currentDirectoryId = currentDirectory?.id ?? '';
  const currentDirectoryName = currentDirectory?.name ?? null;

  const [content, setContent] = useState<DirectoryContent>(EMPTY_CONTENT);
  const lastLoadedIdRef = useRef<string | null>(null);
  const currentDirectoryIdRef = useRef(currentDirectoryId);
  currentDirectoryIdRef.current = currentDirectoryId;
  const loadSequenceRef = useRef(0);
  const directories = content.directories;
  const documentations = content.files;

  const preloadChildDirectories = useCallback(
    (directoryIds: string[]): void => {
      directoryIds.forEach((directoryId) => {
        getDirectoryContent(directoryId).catch(() => undefined);
      });
    },
    [getDirectoryContent]
  );

  useEffect(() => {
    if (!isOpen) return;

    if (initialDirectoryId) {
      setPath([
        {
          id: initialDirectoryId,
          name: initialDirectoryName ?? '',
        },
      ]);
      setSelectedDirectoryId(initialDirectoryId);
    } else {
      setPath([]);
      setSelectedDirectoryId(null);
    }
    setSearchPhrase('');
    setSelectedDocumentationLocalId(initialDocumentationLocalId ?? null);
    setLoadError(null);
    lastLoadedIdRef.current = null;
    // Clear local browser cache when dialog opens to get fresh data
    setBrowserCache(new Map());
  }, [
    isOpen,
    initialDirectoryId,
    initialDirectoryName,
    initialDocumentationLocalId,
  ]);

  useEffect(() => {
    const loadSequence = ++loadSequenceRef.current;
    let isEffectCancelled = false;

    const loadDirectoryContent = async () => {
      if (!isOpen) return;

      const requestedDirectoryId = currentDirectoryId;

      const hasCached = browserCache.has(currentDirectoryId);
      const shouldShowLoading =
        lastLoadedIdRef.current !== currentDirectoryId || !hasCached;

      if (
        !shouldShowLoading &&
        lastLoadedIdRef.current === currentDirectoryId &&
        hasCached
      ) {
        return;
      }

      setLoadError(null);
      if (shouldShowLoading) {
        setLoading(true);
      }
      try {
        const nextContent = await getDirectoryContent(requestedDirectoryId);

        // Ignore stale responses after rapid directory changes.
        if (
          isEffectCancelled ||
          loadSequence !== loadSequenceRef.current ||
          requestedDirectoryId !== currentDirectoryIdRef.current
        ) {
          return;
        }

        const childDirectoryIds = nextContent.directories
          .map((directory) => directory._id)
          .filter(
            (id): id is string => typeof id === 'string' && id.length > 0
          );

        setContent(nextContent);
        lastLoadedIdRef.current = requestedDirectoryId;

        if (childDirectoryIds.length > 0) {
          preloadChildDirectories(childDirectoryIds);
        }
      } catch (e: any) {
        if (
          isEffectCancelled ||
          loadSequence !== loadSequenceRef.current ||
          requestedDirectoryId !== currentDirectoryIdRef.current
        ) {
          return;
        }
        setLoadError(e?.message || 'error');
      } finally {
        if (
          !isEffectCancelled &&
          loadSequence === loadSequenceRef.current &&
          requestedDirectoryId === currentDirectoryIdRef.current &&
          shouldShowLoading
        ) {
          setLoading(false);
        }
      }
    };

    loadDirectoryContent();

    return () => {
      isEffectCancelled = true;
    };
  }, [
    isOpen,
    currentDirectoryId,
    getDirectoryContent,
    preloadChildDirectories,
    browserCache,
  ]);

  const refreshCurrentDirectory = useCallback(() => {
    const activeDirectoryId = currentDirectoryIdRef.current;
    invalidateDirectory(activeDirectoryId);
    lastLoadedIdRef.current = null;
    setLoadError(null);
  }, [invalidateDirectory]);

  const emptyDirectoryIds = useMemo(() => {
    const isEmptyDirectory = (dir: DirectoryModel): boolean => {
      if (!hasValidId(dir)) return false;

      // Check local browser cache for emptiness
      const cached = browserCache.get(dir._id);
      if (cached !== undefined) {
        return cached.directories.length === 0 && cached.files.length === 0;
      }

      return false;
    };

    return new Set(
      directories.filter(isEmptyDirectory).map((dir) => dir._id!)
    );
  }, [directories, browserCache]);

  const goUp = () => {
    setPath((prev) => prev.slice(0, -1));
    setSelectedDirectoryId(null);
    setSelectedDocumentationLocalId(null);
  };

  const enterDirectory = (id: string, name: string) => {
    setPath((prev) => [...prev, { id, name }]);
    setSelectedDirectoryId(null);
    setSelectedDocumentationLocalId(null);
    setContent(EMPTY_CONTENT);
    setLoading(true);
  };

  const handleRowClick = (id: string) => {
    const localId = getDirectoryLocalIdFromRowId(id);
    if (!localId) {
      setSelectedDirectoryId(null);
      return;
    }

    const directory = directories.find((d) => `${d.localId}` === localId);

    if (singleClickOpensDirectory && directory?._id) {
      enterDirectory(directory._id, directory.name);
      return;
    }

    setSelectedDirectoryId(directory?._id ?? null);
  };

  const handleDocumentationRowClick = (id: string) => {
    if (!selectableDocumentation) return;

    const localId = getDocumentationLocalIdFromRowId(id);
    if (!localId) return;

    if (singleDocumentationSelection) {
      setSelectedDocumentationLocalId((prev) =>
        prev === localId ? null : localId
      );
      return;
    }

    setSelectedDocumentationLocalId(localId);
  };

  const handleDoubleClick = (id: string) => {
    if (singleClickOpensDirectory) {
      return;
    }

    const localId = getDirectoryLocalIdFromRowId(id);
    if (!localId) return;

    const directory = directories.find((d) => `${d.localId}` === localId);

    if (directory?._id) {
      enterDirectory(directory._id, directory.name);
    }
  };

  const filteredDirectories = useMemo(
    () => filterItemsByName(directories, searchPhrase),
    [directories, searchPhrase]
  );
  const filteredDocumentations = useMemo(
    () => filterItemsByName(documentations, searchPhrase),
    [documentations, searchPhrase]
  );

  const selectedDocumentation = useMemo(() => {
    if (!selectedDocumentationLocalId) return undefined;

    const matchingDocumentation = documentations.find(
      (documentation) =>
        String(documentation.localId) === selectedDocumentationLocalId
    );

    if (!matchingDocumentation) return undefined;
    if (requireDisplayedVersion && !findDisplayedVersion(matchingDocumentation)) {
      return undefined;
    }

    return matchingDocumentation;
  }, [documentations, selectedDocumentationLocalId, requireDisplayedVersion]);

  const directoryStack = useMemo(() => path.map((p) => p.id), [path]);

  return {
    currentDirectoryId,
    currentDirectoryName,
    directoryStack,
    directories: filteredDirectories,
    emptyDirectoryIds,
    documentations: filteredDocumentations,
    selectedDocumentation,
    selectedDocumentationLocalIds: selectedDocumentationLocalId
      ? [selectedDocumentationLocalId]
      : [],
    loading,
    loadError,
    searchPhrase,
    setSearchPhrase,
    selectedDirectoryId,
    goUp,
    enterDirectory,
    handleRowClick,
    handleDocumentationRowClick,
    handleDoubleClick,
    invalidateDirectory,
    refreshCurrentDirectory,
  };
}
